import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * <ct-thread-kit> - "Canvas Only" / "Canvas + Thread Kit" picker on the PDP
 * (blocks/ct-thread-kit.liquid).
 * - The kit flag and the Thread Requests text are only given a `name` (and so
 *   only submitted with the product form) when the kit card is selected; the
 *   requests text is also left off when blank.
 * - On variant change, swaps in the freshly rendered block (so it appears or
 *   disappears between in-stock and preorder variants) and keeps the shopper's
 *   choice + typed requests. Mirrors assets/pdp-badges.js.
 * @extends {Component}
 */
class CtThreadKit extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#sync);
    this.addEventListener('input', this.#sync);
    this.#sync();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#sync);
    this.removeEventListener('input', this.#sync);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  get #isKit() {
    const kit = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kit-format][value="kit"]'));
    return Boolean(kit?.checked);
  }

  get #requests() {
    return /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[data-kit-requests]'));
  }

  #sync = () => {
    const isKit = this.#isKit;
    const requests = this.#requests;
    setName(this.querySelector('[data-kit-flag]'), isKit);
    setName(requests, isKit && Boolean(requests?.value.trim()));
    this.classList.toggle('is-kit', isKit);
  };

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const fresh = detail.html.querySelector(`ct-thread-kit[data-block-id="${this.dataset.blockId}"]`);
        if (!fresh) return;
        const wasKit = this.#isKit;
        const typed = this.#requests?.value ?? '';
        this.innerHTML = fresh.innerHTML;
        if (wasKit) {
          const kit = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kit-format][value="kit"]'));
          if (kit) kit.checked = true;
        }
        const requests = this.#requests;
        if (requests && typed) requests.value = typed;
        this.#sync();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ct-thread-kit] Event promise rejected:', error);
      });
  };
}

/**
 * @param {Element | null} el
 * @param {boolean} on
 */
function setName(el, on) {
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
  if (on && el.dataset.prop) el.name = el.dataset.prop;
  else el.removeAttribute('name');
}

if (!customElements.get('ct-thread-kit')) {
  customElements.define('ct-thread-kit', CtThreadKit);
}
